import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { useSpringCarousel } from 'react-spring-carousel';

import { Video } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import { ResizableImagePlaceholder } from '@containers/ProductDetailContainer/ProductDetail/ProductImageGallery/ImageSlider/ResizableImagePlaceholder';
import { getVideoIframe } from '@containers/ProductDetailContainer/utils';
import { IImageProperties } from '@helpers/utils';

import { useNewPdDesignEnabled } from '../../hooks/useNewPdDesignEnabled';
import { ModiFaceOverlay } from '../ModiFaceOverlay';

import { ProgressBar } from './ProgressBar';
import { ImageSliderWrapper, PlayerWrapper, StyledArrow } from './styled';
import { useTrackSwiping } from './useTrackSwiping';

export interface IImageSliderProps {
  imagesAttrs: IImageProperties[];
  videos?: Video[];
  hasModiface?: boolean;
  openModifaceModal?: () => void;
}

export const ImageSlider: React.FC<IImageSliderProps> = React.memo(
  ({ imagesAttrs, videos = [], hasModiface, openModifaceModal }) => {
    const newPdDesign = useNewPdDesignEnabled();
    const { trackSwiping } = useTrackSwiping();
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [inViewRef, inView] = useInView({ triggerOnce: true });

    const items: Array<{ id: string; renderItem: JSX.Element }> = React.useMemo(
      () => [
        ...imagesAttrs
          .map((imgProps, index) => {
            const showModifaceOverlay = hasModiface && index === 0;

            // if modiface overlay on mobile, clone main image so customer can swipe to image a view it without overlay
            if (showModifaceOverlay) {
              return [
                {
                  id: `${imgProps.src}-modiface`,
                  renderItem: (
                    <ResizableImagePlaceholder
                      initialInView={true}
                      imgProps={imgProps}
                    >
                      <ModiFaceOverlay onClick={openModifaceModal} />
                    </ResizableImagePlaceholder>
                  ),
                },
                {
                  id: imgProps.src,
                  renderItem: <ResizableImagePlaceholder imgProps={imgProps} />,
                },
              ];
            }

            return {
              id: imgProps.src,
              renderItem: (
                <ResizableImagePlaceholder
                  initialInView={index === 0}
                  imgProps={imgProps}
                />
              ),
            };
          })
          .flat(),

        ...videos.map((video) => ({
          id: video.id,
          renderItem: (
            <PlayerWrapper
              newPdDesign={newPdDesign}
              dangerouslySetInnerHTML={{
                __html: getVideoIframe(video),
              }}
            />
          ),
        })),
      ],
      [hasModiface, imagesAttrs, newPdDesign, openModifaceModal, videos]
    );

    const {
      carouselFragment,
      useListenToCustomEvent,
      slideToNextItem,
      slideToPrevItem,
    } = useSpringCarousel({
      items,
      touchAction: 'pan-y pinch-zoom',
      draggingSlideTreshold: inView ? 10 : undefined,
    });

    useListenToCustomEvent((event) => {
      if (event.eventName === 'onDrag') {
        trackSwiping('swipe');
      } else if (event.eventName === 'onSlideStartChange') {
        setCurrentIndex(event.nextItem.index);
      }
    });

    const onArrowClick = (direction: 'left' | 'right') => {
      const slide = direction === 'left' ? slideToPrevItem : slideToNextItem;
      slide();
      trackSwiping('button');
    };

    const hasNext = currentIndex < items.length - 1;
    const hasPrev = currentIndex > 0;

    return (
      <ErrorBoundary>
        <ImageSliderWrapper ref={inViewRef} newPdDesign={newPdDesign}>
          {hasPrev && (
            <StyledArrow
              direction="left"
              newPdDesign={newPdDesign}
              onClick={() => onArrowClick('left')}
            />
          )}

          {carouselFragment}

          <ProgressBar count={items.length} index={currentIndex} />

          {hasNext && (
            <StyledArrow
              data-testid="chevron-right"
              direction="right"
              newPdDesign={newPdDesign}
              onClick={() => onArrowClick('right')}
            />
          )}
        </ImageSliderWrapper>
      </ErrorBoundary>
    );
  }
);
